import {
  AnyAction,
  configureStore,
  Reducer,
  StoreEnhancer,
} from '@reduxjs/toolkit';
import { ToolkitStore } from '@reduxjs/toolkit/dist/configureStore';
import { routerMiddleware } from 'connected-react-router';
import { createBrowserHistory } from 'history';
import { compact, merge } from 'lodash-es';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import persistState, { mergePersistedState } from 'redux-localstorage';
import adapter from 'redux-localstorage/lib/adapters/localStorage';
import filter from 'redux-localstorage-filter';
import { createLogger } from 'redux-logger';
import { ThunkDispatch } from 'redux-thunk';

import trackingMiddleware from './common/tracking/trackingReduxMiddleware';
import config from './config';
import api from './middleware/api';
import apm from './middleware/apm';
import googleTagManager from './middleware/google-tag-manager';
import rootReducer from './reducers';

const appName = 'glintsEmployersApp';

export const browserHistory = createBrowserHistory();

// Create a reducer that rehydrates from persisted state.
export const createReducer = (reducer: Reducer, asyncReducers?: any) =>
  mergePersistedState((initialState, persistedState) =>
    merge({}, initialState, persistedState)
  )(reducer(browserHistory, asyncReducers));

const storage = filter(['session.token', 'session.data', 'user.me.value'])(
  adapter(window.localStorage)
);

// This will be used to enable redux devtools on production for debugging purposes
const enableReduxDevTools = window?.sessionStorage?.getItem
  ? window.sessionStorage.getItem('_____redux___glints____')
  : null;

const store: ToolkitStore & { asyncReducers?: any } = configureStore({
  reducer: createReducer(rootReducer),
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: false,
    }).concat(
      ...compact([
        api,
        apm,
        trackingMiddleware,
        googleTagManager,
        routerMiddleware(browserHistory),
        config.LOG_REDUX_ACTIONS_TO_CONSOLE &&
          createLogger({ collapsed: true }),
      ])
    ),
  enhancers: [
    // TypeScript is complaining because persistState returns a more generic StoreEnhancer
    // We explicitly cast it as StoreEnhancer to satisfy the type.
    persistState(storage, appName) as unknown as StoreEnhancer,
  ],
  devTools: enableReduxDevTools ?? config.DEBUG,
});

store.asyncReducers = {};

export function injectAsyncReducer(name: string, asyncReducer: Reducer) {
  store.asyncReducers[name] = asyncReducer;
  store.replaceReducer(createReducer(rootReducer, store.asyncReducers));
}

export type RootState = ReturnType<typeof store.getState>;
// The explicit ToolkitStore annotation strips thunk support from the inferred
// dispatch type, so we intersect it with ThunkDispatch to allow dispatching
// thunk action creators (e.g. fetchMe) without casting to any.
export type AppDispatch = typeof store.dispatch &
  ThunkDispatch<RootState, unknown, AnyAction>;

// Type-safe version of useDispatch and useSelector, see https://redux-toolkit.js.org/tutorials/typescript
export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export default store;
